import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { accentPalette } from "@estate/tokens/color";
import { useChat } from "./chat.ts";
import { Composer } from "./components/Composer.tsx";
import { Consent } from "./components/Consent.tsx";
import { Header } from "./components/Header.tsx";
import { LeadCard } from "./components/LeadCard.tsx";
import { Messages } from "./components/Messages.tsx";
import { Welcome } from "./components/Welcome.tsx";
import { ApiError, configureTurnstile, fetchConfig, prepareConversation, track } from "./lib/api.ts";
import { setLinkHosts } from "./lib/markdown.ts";
import type { Layout, PageInfo } from "../shared/protocol.ts";
import { framed, onParent, requestContext, toParent } from "./lib/bridge.ts";
import type { Card, FollowUp, PropertyCard, WidgetConfig } from "./lib/types.ts";
import styles from "./App.module.css";

const BRAND_URL = "https://getunitivo.com/?utm_source=widget&utm_medium=referral";

/** kiosk: the full page on the client's own address (src/kiosk), not a frame. */
export function App({ kiosk = false }: { kiosk?: boolean }) {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(!framed);
  const chat = useChat(config, open);
  const [consented, setConsented] = useState(chat.consentGiven);
  const pendingAsk = useRef<{ question: string; ref: string | null } | null>(null);
  const input = useRef<HTMLTextAreaElement | null>(null);
  /** What the page around the chat is about; its questions replace the general ones. */
  const [page, setPage] = useState<PageInfo | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    fetchConfig()
      .then((c) => {
        applyTheme(c);
        configureTurnstile(c.turnstile);
        setLinkHosts(c.link_hosts);
        setConfig(c);
        // The frame loads with every page of the site: the base of the funnel.
        if (framed && !kiosk) {
          track("widget_loaded", chat.sessionId);
          void requestContext().then((context) => setPage(context?.page ?? null));
        }
      })
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : "Nie udało się wczytać asystenta."));
  }, []);
  useEffect(load, [load]);

  const needsConsent = Boolean(config && hasConsent(config) && !consented);

  // A framed chat reports "opened" when the visitor opens it; the kiosk is
  // open from the start, so it counts the visit once the assistant is there.
  const counted = useRef(false);
  useEffect(() => {
    if (!kiosk || !config || counted.current) return;
    counted.current = true;
    track("widget_opened", chat.sessionId);
    prepareConversation(chat.sessionId);
  }, [kiosk, config, chat.sessionId]);

  // Messages from the page (embed.js). "ready" goes out once we listen.
  useEffect(() => {
    const off = onParent((message) => {
      if (message.type === "open" || message.type === "layout") applyLayout(message);
      if (message.type === "open") {
        setOpen(true);
        track("widget_opened", chat.sessionId);
        prepareConversation(chat.sessionId);
        // Desktop only: on a phone focusing would throw the keyboard over the chat.
        if (window.matchMedia("(pointer: fine)").matches) requestAnimationFrame(() => input.current?.focus({ preventScroll: true }));
      } else if (message.type === "closed") {
        setOpen(false);
        track("widget_closed", chat.sessionId);
      } else if (message.type === "ask") {
        pendingAsk.current = { question: message.question, ref: message.ref };
        flushAsk();
      } else if (message.type === "page") {
        setPage(message.page);
      }
    });
    toParent({ type: "ready" });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flushAsk = () => {
    const ask = pendingAsk.current;
    if (!ask || !configRef.current || needsConsentRef.current || chatRef.current.streaming) return;
    pendingAsk.current = null;
    void chatRef.current.send(ask.question, ask.ref);
  };
  // The listener above is registered once; these refs give it the current state.
  const configRef = useRef(config);
  const needsConsentRef = useRef(needsConsent);
  const chatRef = useRef(chat);
  configRef.current = config;
  needsConsentRef.current = needsConsent;
  chatRef.current = chat;
  useEffect(flushAsk, [config, needsConsent, chat.streaming]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // An open floor plan takes the first Esc for itself.
      if (event.key === "Escape" && framed && !document.querySelector("dialog[open]")) toParent({ type: "close" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---------- scrolling ----------
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [scrolled, setScrolled] = useState(false);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setScrolled(el.scrollTop > 4);
  };
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.lead, open]);
  // The keyboard shrinks the frame from the bottom: the last answer stays in
  // view above the field, as in a messaging app, unless the visitor scrolled up.
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [config, needsConsent]);

  // Full screen on a phone: a field getting focus means the keyboard is on its
  // way, and the page starts following the visible area at once (embed.js).
  useEffect(() => {
    if (!framed) return;
    const typing = (open: boolean) => (event: FocusEvent) => {
      const field = event.target instanceof HTMLElement && event.target.matches("input, textarea");
      if (field && document.documentElement.hasAttribute("data-fullscreen")) toParent({ type: "keyboard", open });
    };
    const focusIn = typing(true);
    const focusOut = typing(false);
    document.addEventListener("focusin", focusIn);
    document.addEventListener("focusout", focusOut);
    return () => {
      document.removeEventListener("focusin", focusIn);
      document.removeEventListener("focusout", focusOut);
    };
  }, []);

  const send = (text: string) => {
    stick.current = true;
    void chat.send(text);
  };
  const onCard = (card: Card) => {
    track("property_clicked", chat.sessionId, { id: card.id }, chat.conversationId);
    chat.noticeInterest();
  };
  // A plan looked at is interest too: the contact offer waits under the chat.
  const onPlan = (card: PropertyCard) => {
    track("plan_opened", chat.sessionId, { id: card.id }, chat.conversationId);
    chat.noticeInterest();
  };
  const onPlanLead = (card: PropertyCard) => {
    const unit = card.name ? `${card.name}${card.investment ? ` (${card.investment})` : ""}` : card.title;
    const text = `Chcę umówić prezentację: ${unit}`;
    if (chat.openLead({ message: text }, "plan")) {
      stick.current = true;
      return;
    }
    send(text);
  };
  const onPick = (text: string, index: number, fromPage: boolean) => {
    track("suggestion_clicked", chat.sessionId, { kind: fromPage ? "page" : "welcome", index, label: text });
    send(text);
  };
  const onFollowUp = (followUp: FollowUp, index: number) => {
    track("suggestion_clicked", chat.sessionId, { kind: "followup", index, label: followUp.label }, chat.conversationId);
    const text = followUp.message ?? followUp.label;
    // Without the contact form (leadCapture off) the request goes to the chat instead.
    if (followUp.action === "lead" && chat.openLead({ message: text }, "button")) {
      stick.current = true;
      return;
    }
    send(text);
  };

  if (loadError) {
    return (
      <div class={styles.state}>
        <p>{loadError}</p>
        <button type="button" class={styles.stateButton} onClick={load}>
          Spróbuj ponownie
        </button>
      </div>
    );
  }
  if (!config) return <div class={styles.state} aria-busy="true" aria-label="Wczytywanie asystenta" />;

  return (
    <div class={styles.shell}>
      <Header
        config={config}
        canReset={chat.messages.length > 0 && !needsConsent}
        scrolled={scrolled}
        onReset={() => {
          stick.current = true;
          chat.reset();
        }}
        onClose={() => toParent({ type: "close" })}
      />
      {needsConsent ? (
        <Consent
          config={config}
          onAccept={() => {
            chat.giveConsent();
            setConsented(true);
          }}
        />
      ) : (
        <>
          <div class={styles.scroll} ref={scroller} onScroll={onScroll}>
            {chat.messages.length === 0 ? (
              <Welcome config={config} pageSuggestions={page?.suggestions} onPick={onPick} disabled={chat.streaming} />
            ) : (
              <Messages
                messages={chat.messages}
                onRetry={chat.retry}
                onCard={onCard}
                onPlan={onPlan}
                onLead={onPlanLead}
                onFollowUp={onFollowUp}
                onRate={chat.rate}
                waitingText={config.mode === "shop" ? "Szukam w katalogu…" : "Sprawdzam aktualną ofertę…"}
                busy={chat.streaming || chat.lead === "open"}
              />
            )}
            {chat.lead !== "hidden" && chat.lead !== "dismissed" && (
              <LeadCard
                config={config}
                state={chat.lead}
                sessionId={chat.sessionId}
                conversationId={chat.conversationId}
                prefill={chat.leadPrefill}
                onState={chat.setLead}
              />
            )}
          </div>
          <div class={styles.bottom}>
            <Composer
              placeholder={config.widget.placeholder?.trim() || "Napisz wiadomość…"}
              streaming={chat.streaming}
              disabled={false}
              onSend={send}
              onStop={chat.stop}
              inputRef={input}
            />
            {/* The disclaimer lives here, always in view, instead of inside answers. */}
            {(config.widget.disclaimer?.trim() || config.branding) && (
              <p class={styles.note}>
                {config.widget.disclaimer?.trim()}
                {config.widget.disclaimer?.trim() && config.branding && " "}
                {config.branding && (
                  <a class={styles.brand} href={BRAND_URL} target="_blank" rel="noopener">
                    Działa na <strong>UNITIVO</strong>
                  </a>
                )}
              </p>
            )}
          </div>
        </>
      )}
      <p class="visually-hidden" aria-live="polite">
        {!chat.streaming && chat.messages.at(-1)?.role === "assistant" ? "Asystent odpowiedział." : ""}
      </p>
    </div>
  );
}

function hasConsent(config: WidgetConfig): boolean {
  const c = config.widget.consent;
  return Boolean(c && (c.intro || c.sections?.length || c.summary?.length));
}

function applyLayout(layout: Layout) {
  const root = document.documentElement;
  root.toggleAttribute("data-fullscreen", layout.fullscreen);
  root.style.setProperty("--safe-top", `${layout.fullscreen ? layout.safeTop : 0}px`);
  root.style.setProperty("--safe-bottom", `${layout.fullscreen ? layout.safeBottom : 0}px`);
}

function applyTheme(config: WidgetConfig) {
  const theme = config.widget.theme ?? "light";
  const dark = theme === "dark" || (theme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const root = document.documentElement;
  root.dataset.theme = dark ? "dark" : "light";
  const palette = accentPalette(config.widget.primaryColor, dark ? "#1b1d21" : "#ffffff");
  root.style.setProperty("--w-accent", palette.accent);
  root.style.setProperty("--w-on-accent", palette.onAccent);
  root.style.setProperty("--w-accent-strong", palette.accentStrong);
  root.style.setProperty("--w-accent-text", palette.accentText);
  document.title = `Asystent ${config.tenant_name}`;
  // Only the kiosk page has one: the phone's status bar takes the header's colour.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", dark ? "#1b1d21" : "#ffffff");
}
